import { Button, TextField, Box } from "@mui/material";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { localizeDateFull } from "utils/dateFormats";
import { useAppMeta, useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyGetLeadsByResponsibleExportQuery, useLazyGetLeadsByResponsibleQuery } from "store/services/reportApi";
import { exportExcelFile } from "utils/exportExcelFile";

const ACTIVE_TABS = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

export const VendorLeadResponsibleBreakdownScreen = () => {
  const { responsibleId } = useParams();
  const location = useLocation();

  const userName = location.state?.userName || "User";

  const { params } = useCustomParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const keyword = params?.search || "";
  const activeTab = params?.tab || "true";

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Paginated list query
  const [fetchLeads, { data: leadsRes, isLoading, isFetching }] =
    useLazyGetLeadsByResponsibleQuery();

  // Separate export query — isolated cache, never touches leadsRes
  const [fetchLeadsExport] = useLazyGetLeadsByResponsibleExportQuery();

  const baseParams = {
    responsibleId,
    isActive: activeTab,
    keyword,
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  };

  React.useEffect(() => {
    fetchLeads({
      ...baseParams,
      page: currentPage,
      limit: perPage,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    responsibleId,
    activeTab,
    keyword,
    currentPage,
    perPage,
    startDate,
    endDate,
    fetchLeads,
  ]);

  useAppMeta(`${userName} — Lead Breakdown`);

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Vendor Lead Responsible Stats", path: "/app/crm/reports/vendor-lead-responsible" },
    { title: `${userName} — Breakdown` },
  ];


  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await fetchLeadsExport(baseParams).unwrap();

      const fileNameParts = [userName, "Lead Breakdown"];
      if (startDate) fileNameParts.push(`from ${startDate}`);
      if (endDate) fileNameParts.push(`to ${endDate}`);

      exportExcelFile({
        sheetData: result.data,
        fileName: fileNameParts.join(" "),
      });
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      name: "Lead ID",
      selector: (row) => row?.leadId,
      cell: (row) => <div>{row?.leadId ?? "N/A"}</div>,
      minWidth: "130px",
    },
    {
      name: "Name",
      selector: (row) => row?.name,
      cell: (row) => <div>{row?.name ?? "N/A"}</div>,
      minWidth: "150px",
    },
    {
      name: "Company",
      selector: (row) => row?.companyName,
      cell: (row) => (
        <div title={row?.companyName} className="line-clamp-1">
          {row?.companyName ?? "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => <div>{row?.phone ?? "N/A"}</div>,
      minWidth: "140px",
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <div title={row?.email} className="line-clamp-1">
          {row?.email ?? "N/A"}
        </div>
      ),
      minWidth: "180px",
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => <div>{row?.status ?? "N/A"}</div>,
      minWidth: "120px",
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name,
      cell: (row) => <div>{row?.createdBy?.name ?? "N/A"}</div>,
      minWidth: "150px",
    },
    {
      name: "Created At",
      selector: (row) => row?.createdAt,
      cell: (row) => (
        <div title={localizeDateFull(row.createdAt)} className="line-clamp-1">
          {row?.createdAt ? localizeDateFull(row.createdAt) : "N/A"}
        </div>
      ),
      minWidth: "160px",
    },
  ];

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={isFetching || isLoading}
    >
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={ACTIVE_TABS} activeTab={activeTab} />

        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <TableFilter searchLabel="Lead ID / Name / Company / Email / Phone" />

          <Button
            variant="outlined"
            size="small"
            onClick={() => { setStartDate(""); setEndDate(""); }}
            disabled={!startDate && !endDate}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={handleExport}
            disabled={isExporting || isLoading || isFetching}
          >
            {isExporting ? "Exporting..." : "Export Excel"}
          </Button>
        </Box>

        <AppTable
          columns={columns}
          data={leadsRes?.data}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={isLoading || isFetching}
          paginationTotalRows={leadsRes?.totalSize ?? 0}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};